import { CommonModule } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChildren,
  forwardRef,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SeahorseTemplateDirective } from '@seahorse/common';
import { PrimeTemplate } from 'primeng/api';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ShFormControl } from '../form-control/form-control.base';
import { primeSize } from '../models';
import {
  buildOptionItems,
  ShOptionAccessor,
  ShOptionItem,
} from '../select/option-items';

/**
 * Type-ahead: a text field that suggests options while you type, over
 * PrimeNG's `p-autocomplete`. Free text is never a value: only a chosen
 * suggestion is (`forceSelection`). The value is the option's `optionValue`
 * (the option itself without one), like `sh-select`.
 *
 * Local mode filters the given options by label (case-insensitive
 * "contains"):
 *
 *   <sh-typeahead [options]="ports" optionLabel="name" optionValue="id" [(value)]="portId" />
 *
 * Remote mode emits `search` with the typed text and shows whatever the
 * caller then puts in `options`. Leave out `optionValue` here, so a chosen
 * value is the option itself and keeps its label when the list changes:
 *
 *   <sh-typeahead remote [options]="found" optionLabel="name"
 *                 (search)="lookup($event)" [(value)]="port" />
 *
 * Custom rendering of the suggestions: `<ng-template shTemplate="item"
 * let-option>`, which gets the caller's option. The list is appended to the
 * body.
 */
@Component({
  selector: 'sh-typeahead',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    AutoCompleteModule,
    PrimeTemplate,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ShTypeaheadComponent),
      multi: true,
    },
  ],
  host: { class: 'sh-control', '[class.sh-invalid]': 'isInvalid()' },
  template: `
    <p-autocomplete
      [suggestions]="suggestions()"
      optionLabel="label"
      [forceSelection]="true"
      [ngModel]="selectedItem()"
      (ngModelChange)="onModelChange($event)"
      [ngModelOptions]="{ standalone: true }"
      (completeMethod)="onComplete($event.query)"
      (onSelect)="onSelect($event.value)"
      (onClear)="commit(null)"
      [inputId]="controlId()"
      [placeholder]="placeholder() | translate"
      [minLength]="minLength()"
      [delay]="delay()"
      [showClear]="showClear()"
      [size]="pSize()"
      [readonly]="isReadonly()"
      [disabled]="isDisabled()"
      [ariaLabel]="ariaLabel() | translate"
      [showEmptyMessage]="!!emptyMessage()"
      [emptyMessage]="emptyMessage() ? (emptyMessage() | translate) : ''"
      [fluid]="true"
      [appendTo]="appendTo()"
      (onBlur)="markTouched()"
    >
      <ng-template pTemplate="item" let-option>
        <ng-container
          *ngIf="itemTemplate() as itemTpl; else defaultItem"
          [ngTemplateOutlet]="itemTpl"
          [ngTemplateOutletContext]="{ $implicit: option.raw }"
        ></ng-container>
        <ng-template #defaultItem>{{ option.label }}</ng-template>
      </ng-template>
    </p-autocomplete>
  `,
})
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class ShTypeaheadComponent<T = any> extends ShFormControl<unknown> {
  readonly options = input<readonly T[] | null | undefined>();
  readonly optionLabel = input<ShOptionAccessor<T, string>>();
  readonly optionValue = input<ShOptionAccessor<T, unknown>>();
  /** The caller searches: `search` is emitted and `options` shown as they are. */
  readonly remote = input(false, { transform: booleanAttribute });
  /** Characters needed before suggestions show. */
  readonly minLength = input(1);
  /** Milliseconds to wait after the last key press. */
  readonly delay = input(300);
  readonly showClear = input(false, { transform: booleanAttribute });
  /**
   * Where the overlay is appended: the body by default (no clipping by
   * scrolling containers); `self` keeps it inline, which a control inside a
   * popover or drawer needs, or a click in the overlay counts as an outside
   * click and closes its container.
   */
  readonly appendTo = input<'body' | 'self' | HTMLElement>('body');
  /** Translation key shown when nothing matches; none by default. */
  readonly emptyMessage = input<string>();

  /** Remote mode: the text to search for. */
  // eslint-disable-next-line @angular-eslint/no-output-native -- public output name; renaming would change the API
  readonly search = output<string>();

  private readonly _templates = contentChildren(SeahorseTemplateDirective, {
    descendants: true,
  });

  // The text last typed; drives the local filter.
  private readonly _query = signal('');

  // A computed, not a getter: PrimeNG must get the same array until the
  // options (or how they are read) really change.
  protected readonly items = computed(() =>
    buildOptionItems(this.options(), this.optionLabel(), this.optionValue())
  );
  protected readonly suggestions = computed(() => {
    const items = this.items();
    if (this.remote()) return items;
    const query = this._query().trim().toLowerCase();
    return items.filter((item) => item.label.toLowerCase().includes(query));
  });
  // What p-autocomplete shows in the input. The previous object is kept
  // while value and label stay the same (`equal`): a new one would make
  // ngModel write it to the input again, wiping the text being typed when a
  // remote search replaces the options (and re-render it endlessly).
  protected readonly selectedItem = computed<ShOptionItem<unknown> | null>(
    () => {
      const value = this.value();
      if (value === null || value === undefined) return null;
      const found = this.items().find((item) => item.value === value);
      if (found) return found;
      // Remote mode: the chosen option is no longer in the list. Without
      // optionValue the value is the option itself, so it has its label.
      return this.optionValue() === undefined
        ? buildOptionItems(
            [value],
            this.optionLabel() as ShOptionAccessor<unknown, string>,
            undefined
          )[0]
        : null;
    },
    {
      equal: (a, b) =>
        a === b || (!!a && !!b && a.value === b.value && a.label === b.label),
    }
  );
  protected readonly pSize = computed(
    () => primeSize(this.size()) as 'small' | 'large'
  );
  protected readonly itemTemplate = computed(
    () => this._templates().find((t) => t.type === 'item')?.templateRef
  );

  protected onComplete(query: string): void {
    this._query.set(query ?? '');
    if (this.remote()) this.search.emit(query ?? '');
  }

  protected onSelect(item: ShOptionItem<unknown> | null | undefined): void {
    if (item) this.commit(item.value);
  }

  // Typing makes the model a string: free text is never a value, so only an
  // emptied field (cleared) counts here; a chosen item arrives via onSelect.
  protected onModelChange(model: unknown): void {
    if (model === null || model === undefined || model === '') {
      this.commit(null);
    }
  }
}
